/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { isolateComponent, responsiveStyles } from '@zapier/common-utils';
import { Button, Img, Spacer, Text } from '@zapier/design-system';

import { Size } from '@zapier/design-system-revenue/src/types';
import { CurrencyDropdown } from './CurrencyDropdown';

type HeroProps = {
  /** Button link url */
  buttonHref?: string;
  /** Button onClick function */
  buttonOnClick?: () => void;
  /** Button text */
  buttonText: string;
  /** Description text (directly under title) */
  description: string;
  /** Image url at different sizes */
  imageUrl: {
    [x: string]: string;
  };
  /** Size to render */
  size: Size;
  /** Title text */
  title: string;
};

const Styles = {
  root: (size: Size) => [
    css`
      margin: 0 auto;
      max-width: 1200px;
      position: relative;
    `,
    responsiveStyles(size, {
      small: css`
        background: linear-gradient(227.07deg, #ace7f8 0%, #feddc8 51.64%);
        padding: 20px;
      `,
      medium: css`
        background: none;
        padding: 100px 50px 50px 40px;
      `,
      large: css`
        background: none;
        padding: 95px 80px 60px 80px;
      `,
    }),
  ],

  actions: (size: Size) => [
    css`
      align-items: center;
      display: flex;
    `,
    responsiveStyles(size, {
      small: css`
        justify-content: center;
      `,
      medium: css`
        justify-content: left;
      `,
      large: css`
        justify-content: left;
      `,
    }),
  ],

  button: css`
    margin-right: 10px;
  `,

  image: (size: Size) => [
    css`
      position: absolute;
      top: -50px;
    `,
    responsiveStyles(size, {
      small: css``,
      medium: css`
        right: -21px;
        width: 600px;
      `,
      large: css`
        left: 641px;
        width: 600px;
      `,
    }),
  ],

  info: (size: Size) => [
    css`
      position: relative;
      z-index: 2;
    `,
    responsiveStyles(size, {
      small: css`
        max-width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
      `,
      medium: css`
        max-width: 391px;
      `,
      large: css`
        max-width: 460px;
      `,
    }),
  ],
};

const getButtonSpace = (size: Size) =>
  ['medium', 'large'].includes(size) ? 40 : 20;

const getDescriptionTextStyles = (size: Size) =>
  ['medium', 'large'].includes(size)
    ? {
        color: 'black' as 'black',
        type: 'subtitle900' as 'subtitle900',
      }
    : {
        color: 'grey700' as 'grey700',
        type: 'bodyText800' as 'bodyText800',
      };

const getTitleTypography = (size: Size) =>
  ['medium', 'large'].includes(size) ? 'header900' : 'header800';

export const Hero = ({
  buttonHref,
  buttonOnClick,
  buttonText,
  description,
  imageUrl,
  size,
  title,
}: HeroProps) => (
  <div css={Styles.root(size)}>
    {size !== 'small' && (
      <div css={Styles.image(size)}>
        <Img
          alt="Two happy business owners"
          hasRadius={false}
          src={imageUrl[size]}
        />
      </div>
    )}

    <div css={Styles.info(size)}>
      <Text
        tag="h1"
        textAlign={size === 'small' ? 'center' : 'left'}
        type={getTitleTypography(size)}
      >
        {size === 'small' ? title.replace('&', 'and') : title}
      </Text>

      <Spacer height={20} />

      <Text
        tag="p"
        textAlign={size === 'small' ? 'center' : 'left'}
        {...getDescriptionTextStyles(size)}
      >
        {description}
      </Text>

      <Spacer height={getButtonSpace(size)} />

      <div css={Styles.actions(size)}>
        {size === 'large' && (
          <div css={Styles.button}>
            <Button
              hasShadow={true}
              href={buttonHref}
              onClick={buttonOnClick}
              size="small"
            >
              {buttonText}
            </Button>
          </div>
        )}

        <CurrencyDropdown />
      </div>
    </div>
  </div>
);
